import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import qs from 'qs';

import {
  COWORKER_GROUP_QUERY_PARAM,
  FLOORPLAN_VIEW,
  FOCUS_RESOURCE_QUERY_PARAM,
  RESOURCES_VIEW_QUERY_PARAM,
} from 'floorplan/pages/native-floorplan/const';
import { stripCoworkerSheetPath } from './use-coworker-sheet-navigation.hook';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { replace } from 'store/router/actions';
import { searchParams as SearchParams } from 'shared/types/search-params';

interface UseActiveCoworkerGroupValues {
  /** The group the map is focused on (§C3), as the url names it; null when none. */
  activeGroupUuid: string | null;
  /** Focus the map on a group (null clears). Stays on the current route — the sheet, if open. */
  setActiveGroup: (groupUuid: string | null) => void;
  clearActiveGroup: VoidFunction;
  /** The group-level "View": close the sheet, put the map on screen, focused on the group. */
  showGroupOnMap: (groupUuid: string) => void;
}

/**
 * The active coworker group — focus mode — read from and written to the url (`coworkerGroup`), so the
 * sheet's chips and the map agree without a store slice, and a reload keeps the focus.
 *
 * Focusing a group ends a single-desk focus (`focusResource`): the group filter is the broader
 * answer to "where do they sit", and the ring would otherwise linger on one member.
 */
export const useActiveCoworkerGroup = (): UseActiveCoworkerGroupValues => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const queryParams = useSearchParams<SearchParams>();
  const value = queryParams[COWORKER_GROUP_QUERY_PARAM];
  const activeGroupUuid = typeof value === 'string' && value ? value : null;

  const navigateWithGroup = useCallback(
    (targetPathname: string, groupUuid: string | null, extra: Partial<SearchParams> = {}) => {
      // `qs.stringify` leaves undefined values out, which is how a param is dropped.
      const nextParams: Record<string, string | undefined> = {
        ...queryParams,
        ...extra,
        [COWORKER_GROUP_QUERY_PARAM]: groupUuid ?? undefined,
        [FOCUS_RESOURCE_QUERY_PARAM]: undefined,
      };

      dispatch(replace(`${targetPathname}?${qs.stringify(nextParams)}`));
    },
    [dispatch, queryParams],
  );

  const setActiveGroup = useCallback(
    (groupUuid: string | null) => navigateWithGroup(pathname, groupUuid),
    [navigateWithGroup, pathname],
  );

  const clearActiveGroup = useCallback(() => {
    if (activeGroupUuid) {
      navigateWithGroup(pathname, null);
    }
  }, [activeGroupUuid, navigateWithGroup, pathname]);

  const showGroupOnMap = useCallback(
    (groupUuid: string) =>
      navigateWithGroup(stripCoworkerSheetPath(pathname), groupUuid, { [RESOURCES_VIEW_QUERY_PARAM]: FLOORPLAN_VIEW }),
    [navigateWithGroup, pathname],
  );

  return useMemo(
    () => ({ activeGroupUuid, setActiveGroup, clearActiveGroup, showGroupOnMap }),
    [activeGroupUuid, setActiveGroup, clearActiveGroup, showGroupOnMap],
  );
};
